/**
 * Vuex module for the "Build a Kit" feature: fixture catalog, the in-progress
 * wizard selection, and the active Project (the persistent, named cart a rep
 * keeps adding different fixture kits to).
 *
 * Registered as a namespaced module in resources/js/store/store.js. Nothing
 * in the existing root state/getters/actions is touched.
 */
import api from "../../apis/kitApi";
import * as rules from "../../kit-builder/kit-builder-rules.js";

const state = () => ({
    catalog: null, // fetched once, cached for the session
    catalogLoading: false,

    projects: [], // for the project picker / list view
    projectsLoading: false,

    currentProject: null, // full project incl. items, once selected/created

    wizard: {
        engine: null, // 'bespoke3in' | 'fd' | 'generic'
        familyKey: null,
        sizeLabel: null,
        fixtureType: null, // 'can' | 'canless', bespoke3in/fd only
        sel: {},
        accessories: [],
        editingItemId: null, // set when re-opening an existing line item to edit
    },
});

const getters = {
    genericFamily: (state) => (key) =>
        state.catalog ? state.catalog.genericEngineFamilies.find((f) => f.key === key) : null,

    currentGenericFamily: (state, getters) =>
        state.wizard.engine === "generic" ? getters.genericFamily(state.wizard.familyKey) : null,

    currentFdFamily: (state) => {
        if (state.wizard.engine !== "fd" || !state.catalog) return null;
        const table =
            state.wizard.fixtureType === "canless"
                ? state.catalog.canlessFdFamilies
                : state.catalog.recessedFdFamilies;
        return table[state.wizard.sizeLabel] || null;
    },

    /** Option-gating check for the currently active wizard family (generic engine only). */
    isOptionDisabled: (state) => (stepKey, code) => {
        if (state.wizard.engine !== "generic") return false;
        return rules.genOptionDisabled(state.wizard.familyKey, stepKey, code, state.wizard.sel);
    },

    /** Live unit price for whatever's currently selected in the wizard. */
    currentUnitPrice: (state, getters) => {
        if (state.wizard.engine === "generic" && getters.currentGenericFamily) {
            return rules.genComputeUnitPrice(
                getters.currentGenericFamily,
                state.wizard.sel,
                state.wizard.accessories,
                state.catalog.secondaryOptics
            );
        }
        if (state.wizard.engine === "fd" && getters.currentFdFamily) {
            return rules.fdComputeUnitPrice(
                getters.currentFdFamily,
                state.wizard.sel,
                state.wizard.fixtureType,
                state.catalog.secondaryOptics
            );
        }
        if (state.wizard.engine === "bespoke3in" && state.catalog) {
            return rules.computeUnitPrice3in(
                state.wizard.sel,
                state.wizard.fixtureType,
                state.catalog.bespoke3in,
                state.catalog.bespoke3in.SECONDARY_OPTICS
            );
        }
        return 0;
    },

    /**
     * Live per-item breakdown for the wizard preview -- the same split the
     * server stores on save, so the sidebar shows the individual housing /
     * module / trim prices rather than one combined figure.
     */
    currentComponents: (state, getters) => {
        if (state.wizard.engine === "generic" && getters.currentGenericFamily) {
            return rules.genComputeComponents(
                getters.currentGenericFamily,
                state.wizard.sel,
                state.wizard.accessories,
                state.catalog.secondaryOptics
            );
        }
        if (state.wizard.engine === "fd" && getters.currentFdFamily) {
            return rules.fdComputeComponents(
                getters.currentFdFamily,
                state.wizard.sel,
                state.wizard.fixtureType,
                state.catalog.secondaryOptics
            );
        }
        if (state.wizard.engine === "bespoke3in" && state.catalog) {
            return rules.computeComponents3in(
                state.wizard.sel,
                state.wizard.fixtureType,
                state.catalog.bespoke3in,
                state.catalog.bespoke3in.SECONDARY_OPTICS
            );
        }
        return [];
    },

    /** Live part number(s) for whatever's currently selected in the wizard. */
    currentPartNumbers: (state, getters) => {
        if (state.wizard.engine === "generic" && getters.currentGenericFamily) {
            return { combined: rules.genBuildPartNumber(getters.currentGenericFamily, state.wizard.sel) };
        }
        if (state.wizard.engine === "fd" && getters.currentFdFamily) {
            return rules.fdBuildPartNumbers(getters.currentFdFamily, state.wizard.sel, state.wizard.fixtureType);
        }
        if (state.wizard.engine === "bespoke3in" && state.catalog) {
            return rules.buildPartNumbers3in(
                state.wizard.sel,
                state.wizard.fixtureType,
                state.catalog.bespoke3in.MOUNT_ACCESSORY
            );
        }
        return {};
    },
};

const mutations = {
    SET_CATALOG(state, catalog) {
        state.catalog = catalog;
    },
    SET_CATALOG_LOADING(state, v) {
        state.catalogLoading = v;
    },

    SET_PROJECTS(state, projects) {
        state.projects = projects;
    },
    SET_PROJECTS_LOADING(state, v) {
        state.projectsLoading = v;
    },
    SET_CURRENT_PROJECT(state, project) {
        state.currentProject = project;
    },

    UPSERT_ITEM_IN_CURRENT_PROJECT(state, item) {
        if (!state.currentProject) return;
        if (!state.currentProject.items) state.currentProject.items = [];
        const idx = state.currentProject.items.findIndex((i) => i.id === item.id);
        if (idx === -1) state.currentProject.items.push(item);
        else state.currentProject.items.splice(idx, 1, item);
    },
    REMOVE_ITEM_FROM_CURRENT_PROJECT(state, itemId) {
        if (!state.currentProject) return;
        state.currentProject.items = (state.currentProject.items || []).filter((i) => i.id !== itemId);
    },

    START_WIZARD(state, { engine, familyKey, sizeLabel = null, fixtureType = null }) {
        state.wizard = {
            engine,
            familyKey,
            sizeLabel,
            fixtureType,
            sel: {},
            accessories: [],
            editingItemId: null,
        };
    },
    LOAD_ITEM_INTO_WIZARD(state, item) {
        state.wizard = {
            engine: item.engine,
            familyKey: item.family_key,
            sizeLabel: item.size_label,
            fixtureType: item.fixture_type,
            sel: { ...item.selections },
            accessories: [...(item.accessories || [])],
            editingItemId: item.id,
        };
    },
    SET_WIZARD_SELECTION(state, { key, value }) {
        state.wizard.sel = { ...state.wizard.sel, [key]: value };
    },
    CLEAR_WIZARD_SELECTION(state, key) {
        const sel = { ...state.wizard.sel };
        delete sel[key];
        state.wizard.sel = sel;
    },
    SET_WIZARD_ACCESSORIES(state, accessories) {
        state.wizard.accessories = accessories;
    },
    RESET_WIZARD(state) {
        state.wizard = {
            engine: null,
            familyKey: null,
            sizeLabel: null,
            fixtureType: null,
            sel: {},
            accessories: [],
            editingItemId: null,
        };
    },
};

const actions = {
    async fetchCatalog({ commit, state }) {
        if (state.catalog) return state.catalog; // cache for the session
        commit("SET_CATALOG_LOADING", true);
        try {
            const catalog = await api.getCatalog();
            commit("SET_CATALOG", catalog);
            return catalog;
        } finally {
            commit("SET_CATALOG_LOADING", false);
        }
    },

    async fetchProjects({ commit }, params) {
        commit("SET_PROJECTS_LOADING", true);
        try {
            const res = await api.listProjects(params);
            commit("SET_PROJECTS", res.data || []);
            return res;
        } finally {
            commit("SET_PROJECTS_LOADING", false);
        }
    },

    async createProject({ commit }, payload) {
        const project = await api.createProject(payload);
        commit("SET_CURRENT_PROJECT", project);
        return project;
    },

    async loadProject({ commit }, projectId) {
        const project = await api.getProject(projectId);
        commit("SET_CURRENT_PROJECT", project);
        return project;
    },

    async renameProject({ commit, state }, payload) {
        const project = await api.updateProject(state.currentProject.id, payload);
        commit("SET_CURRENT_PROJECT", project);
        return project;
    },

    async deleteProject({ commit, state }, projectId) {
        await api.deleteProject(projectId);
        if (state.currentProject && state.currentProject.id === projectId) {
            commit("SET_CURRENT_PROJECT", null);
        }
    },

    startWizard({ commit }, opts) {
        commit("START_WIZARD", opts);
    },

    resetWizard({ commit }) {
        commit("RESET_WIZARD");
    },

    editItem({ commit }, item) {
        commit("LOAD_ITEM_INTO_WIZARD", item);
    },

    /**
     * Mirrors the prototype's setGenSel() re-validation: after changing one
     * field, clear any other already-picked field that's no longer a legal
     * combination under the gating rules (generic engine only -- the FD and
     * 3" bespoke engines gate visually via their own step-sequencing instead).
     */
    setSelection({ commit, state, getters }, { key, value }) {
        commit("SET_WIZARD_SELECTION", { key, value });
        if (state.wizard.engine !== "generic") return;
        const fam = getters.currentGenericFamily;
        if (!fam) return;
        for (const step of fam.steps) {
            if (step.key === key) continue;
            const picked = state.wizard.sel[step.key];
            if (picked && rules.genOptionDisabled(fam.key, step.key, picked, state.wizard.sel)) {
                commit("CLEAR_WIZARD_SELECTION", step.key);
            }
        }
    },

    setAccessories({ commit }, accessories) {
        commit("SET_WIZARD_ACCESSORIES", accessories);
    },

    /**
     * "Add Kit to Order" -- adds (or, if state.wizard.editingItemId is set,
     * updates) a line item on the CURRENT project.
     *
     * It deliberately does NOT reset the wizard: clearing `wizard.engine` here
     * unmounts the wizard component on the very next scheduler flush, which
     * lands between this action resolving and the caller's `await` resuming --
     * so the component's "saved" event was emitted from an already-unmounted
     * instance, never reached the panel, and the screen stayed blank. The panel
     * resets the wizard itself once it has switched views (see goToCart).
     */
    async addOrUpdateCurrentKit({ commit, state }, quantity = 1) {
        if (!state.currentProject) {
            throw new Error("No active project -- call createProject() or loadProject() first.");
        }
        const payload = {
            engine: state.wizard.engine,
            family_key: state.wizard.familyKey,
            size_label: state.wizard.sizeLabel,
            fixture_type: state.wizard.fixtureType,
            selections: state.wizard.sel,
            accessories: state.wizard.accessories,
            quantity,
        };

        const item = state.wizard.editingItemId
            ? await api.updateItem(state.currentProject.id, state.wizard.editingItemId, payload)
            : await api.addItem(state.currentProject.id, payload);

        commit("UPSERT_ITEM_IN_CURRENT_PROJECT", item);
        return item;
    },

    async updateItemQuantity({ commit, state }, { itemId, quantity }) {
        const item = await api.updateItem(state.currentProject.id, itemId, { quantity });
        commit("UPSERT_ITEM_IN_CURRENT_PROJECT", item);
        return item;
    },

    async removeItem({ commit, state }, itemId) {
        await api.removeItem(state.currentProject.id, itemId);
        commit("REMOVE_ITEM_FROM_CURRENT_PROJECT", itemId);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
